import _ from 'lodash';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderAPI } from '../../apis/order-api';
import { useSpotAPI } from '../../apis/spot-api';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { sortSpots, useSpotManager } from '../../utils/manager/spot-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';

function useSelectSpotModal() {
  const { setSpot } = useStorage();
  const storage = useStorage();
  const { analyticsPageView } = useAnalytics();
  const { handleErrorOnModal } = useErrorHandler();
  const { findSpotsFromRemote } = useSpotAPI();
  const { checkCurrentSpot, checkSpotChange } = useSpotManager();
  const { findSpotOrdersFromRemote } = useOrderAPI();

  const spotRef = useRef(null);
  const spotsRef = useRef(null);
  const { swal } = useSwal({ storage });
  const { t } = useTranslation();

  function didOpen() {
    analyticsPageView('Spot Selection');
  }

  async function prepare() {
    try {
      const response = await findSpotOrdersFromRemote();
      await checkSpotChange(response.data);
    } catch (error) {
      if (
        error.response?.status === 422 &&
        error.response?.data?.find((v) => v.code === 1) != null
      ) {
        setSpot(null);
      }
    }

    const spotResponse = await checkCurrentSpot();
    const spot = spotResponse.data;
    spotRef.current = spot;

    if (spot == null) {
      const spotsResponse = await findSpotsFromRemote();
      spotsRef.current = sortSpots(spotsResponse.data);
    }
  }

  async function show({ prepareOnPreConfirm }) {
    // if (spotRef.current == null) {
    const result = await swal.fire({
      icon: 'warning',
      title: t('select-spot-modal.delivery-question'),
      didOpen,
      input: 'select',
      inputPlaceholder: t('select-spot-modal.choose-one'),
      inputOptions: getOptions(),
      inputValidator,
      showCancelButton: true,
      cancelButtonText: t('cancel'),
      showLoaderOnConfirm: true,
      preConfirm: (id) => preConfirm({ id, prepareOnPreConfirm }),
    });

    return result;
  }

  function getOptions() {
    const options = {};

    spotsRef.current.forEach((spot) => {
      const inUse = spot.share_bills === true && spot.open_bills > 0;
      options[spot.id] =
        spot.name + (inUse ? ` (${t('select-spot-modal.busy')})` : '');
    });

    return options;
  }

  function inputValidator(data) {
    if (_.isEmpty(data?.trim())) {
      return t('select-spot-modal.choose-one');
    }

    return null;
  }

  async function preConfirm({ id, prepareOnPreConfirm }) {
    const spot = spotsRef.current.find((s) => s.id === id);
    setSpot(spot);
    spotRef.current = spot;

    try {
      await prepareOnPreConfirm?.call(this);
    } catch (error) {
      if (error.response?.status !== 422) {
        handleErrorOnModal(error);
      }

      return false;
    }

    return true;
  }

  return {
    prepareSelectSpotModal: prepare,
    showSelectSpotModal: show,
  };
}

export { useSelectSpotModal };
